import { Flex, Text } from "@jsq/ds/core";
import { QuestionType } from "~/graphql/supergraphEnums";
import { QUESTION_TYPE_LABELS } from "~/pages/ddq/intake/DDQueue/labels";
import { SPACING } from "~/styles/variables";
import * as styles from "./styles_vanilla.css";
import type { GetHistoricalDdqAnswersQuery } from "~/graphql/supergraphTypes";

export type HistoricalDdqAnswer = GetHistoricalDdqAnswersQuery["historicalDdqAnswers"][number];

export type QaEntryRowProps = {
  entry: HistoricalDdqAnswer;
};

// Mirrors AnswerQaView/QuestionAnswerRow.tsx's displayValue: TEXT questions carry their answer in
// textAnswer directly; every other question type round-trips through value as JSON. Returns null
// (rather than "") for an extracted-but-unanswered question, so the caller can omit the <Text>
// node instead of rendering a blank line.
const displayAnswer = (entry: HistoricalDdqAnswer): string | null => {
  if (entry.questionType === QuestionType.Text) {
    return entry.textAnswer;
  }
  return entry.value != null ? JSON.stringify(entry.value) : null;
};

export const QaEntryRow = ({ entry }: QaEntryRowProps) => {
  return (
    <Flex
      flexDirection="column"
      gap={SPACING.X_SMALL}
      data-testid={`historical-qa-entry-${entry.id}`}
    >
      <Flex justifyContent="space-between" alignItems="flex-start" gap={SPACING.SMALL}>
        <Text variant="strong-sm">{entry.questionText}</Text>
        <Text
          variant="regular-sm"
          className={styles.questionTypeLabel}
          data-testid={`historical-qa-entry-type-${entry.id}`}
        >
          {QUESTION_TYPE_LABELS[entry.questionType]}
        </Text>
      </Flex>
      {displayAnswer(entry) && (
        <Text variant="regular-md" data-testid={`historical-qa-entry-value-${entry.id}`}>
          {displayAnswer(entry)}
        </Text>
      )}
      <Text
        variant="regular-sm"
        className={entry.approvedForAnswerBank ? styles.includedText : styles.excludedText}
        data-testid={`historical-qa-entry-included-${entry.id}`}
      >
        {entry.approvedForAnswerBank ? "Included" : "Excluded"}
      </Text>
    </Flex>
  );
};
